import { FiArrowRight } from 'react-icons/fi';

export const futureFormsData = {
  courseId: 'future-forms',
  courseType: 'grammar',
  title: 'Future Forms (will / going to)',
  icon: FiArrowRight,
  overview: {
    impact: 'Express predictions, plans, and intentions accurately',
    focus: 'will vs going to vs present continuous for future',
    uses: 'Speaking Part 3, Writing Task 2 predictions',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Will',
      content: `<p>Use <strong>will + base verb</strong> for:</p>`,
      points: [
        'Predictions based on opinion: \'I think it will rain.\'',
        'Spontaneous decisions: \'I\'ll help you with that.\'',
        'Promises/offers: \'I will call you tomorrow.\'',
        'General future facts: \'The sun will rise at 6 am.\''
      ]
    },
    {
      title: 'Going to',
      content: `<p>Use <strong>am/is/are going to + base verb</strong> for:</p>`,
      points: [
        'Plans already decided: \'I\'m going to study medicine.\'',
        'Predictions based on evidence: \'Look at those clouds — it\'s going to rain.\''
      ],
      examples: [
        {
          label: 'Plan',
          text: 'We are going to move to a new house next month.'
        },
        {
          label: 'Evidence',
          text: 'She\'s been studying hard — she\'s going to pass the exam.'
        }
      ]
    },
    {
      title: 'Present Continuous for Future',
      content: `<p>Use present continuous for <strong>fixed arrangements</strong>:</p>`,
      examples: [
        {
          label: 'Arrangement',
          text: 'I\'m meeting John at 3 pm tomorrow.'
        },
        {
          label: 'Travel',
          text: 'We\'re flying to Barcelona on Friday.'
        }
      ],
      warning: 'Only use this for arrangements with other people or booked plans, not general intentions.'
    }
  ],
  exercises: [
    {
      title: 'Will or Going To?',
      type: 'select',
      instructions: 'Choose the most appropriate future form.',
      questions: [
        {
          text: 'I\'ve decided — I ___ study abroad next year.',
          options: [
            'am going to',
            'will',
            'am'
          ],
          answer: 'am going to',
          explanation: 'Decided plan → \'going to\'.'
        },
        {
          text: 'I think the economy ___ improve next year.',
          options: [
            'will',
            'is going to',
            'is'
          ],
          answer: 'will',
          explanation: 'Opinion-based prediction → \'will\'.'
        },
        {
          text: 'Look at the sky! It ___ snow.',
          options: [
            'is going to',
            'will',
            'does'
          ],
          answer: 'is going to',
          explanation: 'Evidence-based prediction → \'going to\'.'
        },
        {
          text: 'Someone\'s at the door. I ___ get it.',
          options: [
            'will',
            'am going to',
            'am getting'
          ],
          answer: 'will',
          explanation: 'Spontaneous decision → \'will\'.'
        }
      ]
    }
  ],
  tips: [
    '\'Will\' = opinions, promises, spontaneous decisions',
    '\'Going to\' = plans, evidence-based predictions',
    'Present continuous = fixed arrangements/bookings',
    'In essays, \'will\' is most common for predictions about the future'
  ],
  dailyPractice: [
    'Practice future forms (will / going to) concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Future Forms: will for predictions/spontaneous decisions, going to for plans/evidence-based predictions, present continuous for arrangements. Key for IELTS Speaking Part 3 and essay predictions.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...futureFormsData,
      title: futureFormsData.title + ' (Foundations)',
      overview: { ...futureFormsData.overview, duration: '15-20 minutes' },
      sections: futureFormsData.sections.slice(0, Math.max(2, Math.ceil(futureFormsData.sections.length / 2))),
      exercises: futureFormsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...futureFormsData,
      title: futureFormsData.title + ' (Advanced)',
      overview: { ...futureFormsData.overview, duration: '30-35 minutes' },
      sections: futureFormsData.sections,
      exercises: futureFormsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(futureFormsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return futureFormsData;
}
